// Network page: animated stylized world route map (arcs draw on scroll-into-view)
// + domestic ports list + international lanes. Content from company-profile/content.md
// via the `network` namespace. No tile provider/API key — SVG only. Owned by Agent D.
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DEFAULT_LOCALE } from "../i18n";
import { fadeUp, stagger, revealViewport } from "../motion/variants";
import PageHero from "../components/PageHero";
import SectionBlock from "../components/SectionBlock";
import RouteMap from "../components/RouteMap";
import PageMeta from "../components/PageMeta";

export default function Network() {
  const { t } = useTranslation("network");
  const { lang } = useParams();
  const current = lang ?? DEFAULT_LOCALE;

  const ports = t("domestic.ports", { returnObjects: true }) as string[];
  const lanes = t("international.lanes", { returnObjects: true }) as {
    region: string;
    desc: string;
  }[];

  return (
    <div className="page page-network">
      <PageMeta title={t("meta.title")} description={t("meta.description")} lang={current} />

      <PageHero
        eyebrowEn={t("hero.eyebrowEn")}
        title={t("hero.title")}
        subtitle={t("hero.subtitle")}
      />

      {/* Animated route map */}
      <section className="bg-white py-[var(--space-section)]">
        <div className="mx-auto w-full max-w-5xl px-6">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
            className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)] p-6 md:p-10"
          >
            <RouteMap hubLabel={t("map.hubLabel")} ariaLabel={t("map.ariaLabel")} lang={current} />
            <p className="mt-4 text-center text-[var(--text-sm)] text-[var(--brand-slate-text)]">
              {t("map.caption")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Domestic ports */}
      <SectionBlock
        eyebrowEn={t("domestic.eyebrowEn")}
        heading={t("domestic.title")}
        lead={t("domestic.lead")}
        surface="tint"
      >
        <motion.ul
          variants={stagger}
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        >
          {ports.map((port) => (
            <motion.li
              key={port}
              variants={fadeUp}
              className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-white px-4 py-3 text-[var(--text-base)] font-medium text-[var(--ink)]"
            >
              <span
                className="h-2 w-2 flex-none rounded-full bg-[var(--brand-primary)]"
                aria-hidden="true"
              />
              {port}
            </motion.li>
          ))}
        </motion.ul>
      </SectionBlock>

      {/* International lanes */}
      <SectionBlock
        eyebrowEn={t("international.eyebrowEn")}
        heading={t("international.title")}
        lead={t("international.lead")}
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {lanes.map((lane) => (
            <motion.div
              key={lane.region}
              variants={fadeUp}
              className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6"
            >
              <h3 className="flex items-center gap-3 font-zh text-[var(--text-xl)] font-bold text-[var(--brand-azure)]">
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6 flex-none text-[var(--brand-primary)]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3.5 12h17M12 3c2.5 2.4 2.5 15.6 0 18M12 3c-2.5 2.4-2.5 15.6 0 18" />
                </svg>
                {lane.region}
              </h3>
              <p className="mt-3 text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
                {lane.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </SectionBlock>
    </div>
  );
}
